import React from 'react';
import styles from '../../styles/UserPage.module.css';
import { PROVIDER_LABELS } from '../../utils/modelConstants';

const TOOL_LABELS = {
  tavily: 'Tavily Search',
  you: 'You Search',
};

// Production refuses a private address, so its example can't be one.
const localServerExample = () => (
  process.env.NODE_ENV === 'production'
    ? 'https://your-server.example.com'
    : 'http://localhost:11434'
);

const ApiKeysTab = ({
  isLoading,
  selectedApiProvider,
  setSelectedApiProvider,
  API_PROVIDERS,
  configuredProviders,
  providerConfigs,
  handleAddProviderConfig,
  handleDeleteProviderConfig,
  handleConfigFieldChange,
  handleClearConfigField,
  getInputValue,
  setFocusedInput,
  isMasked,
  PROVIDER_CREDENTIAL_FIELDS,
  toolApiKeys,
  toolApiKeyInputs,
  setToolApiKeyInputs,
  handleSaveToolApiKey,
  handleRemoveToolApiKey,
}) => {
  return (
    <div className={`${styles.section} ${styles.apiKeysSection}`} style={{ marginTop: '0' }}>
      <h2 className={styles.sectionTitle}>API Keys</h2>
      <p className={styles.sectionDescription}>
        Add one or more configurations per provider. Each configuration has its own API key, endpoint, and credentials. Use the label field to name each config (e.g. "GPT endpoint", "Claude endpoint").
      </p>

      {isLoading ? (
        <p>Loading...</p>
      ) : (
        (() => {
          const provider = selectedApiProvider || API_PROVIDERS[0];
          const emptyConfig = (configIndex = 1, configName = '') => ({
            config_index: configIndex,
            config_name: configName,
            api_key: '',
            endpoint: '',
            access_key: '',
            secret_key: '',
            region: '',
          });
          const cfgList = providerConfigs[provider] || [emptyConfig(1)];
          const ownCreds = PROVIDER_CREDENTIAL_FIELDS[provider] || [];
          return (
            <>
              <div style={{ marginBottom: '0.8rem' }}>
                <label className={styles.label} style={{ marginBottom: '0.35rem', display: 'block' }}>
                  Provider
                </label>
                <select
                  value={provider}
                  onChange={(e) => setSelectedApiProvider(e.target.value)}
                  className={styles.input}
                >
                  {API_PROVIDERS.map((prov) => {
                    const provLabel = PROVIDER_LABELS[prov] || (prov.charAt(0).toUpperCase() + prov.slice(1));
                    return (
                      <option key={prov} value={prov}>
                        {provLabel}
                      </option>
                    );
                  })}
                </select>
                <div style={{ marginTop: '0.45rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem 0.75rem' }}>
                  {API_PROVIDERS.map((prov) => {
                    const isConfigured = configuredProviders.includes(prov);
                    const provLabel = PROVIDER_LABELS[prov] || (prov.charAt(0).toUpperCase() + prov.slice(1));
                    return (
                      <span
                        key={`${prov}-status`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.35rem',
                          fontSize: '0.75rem',
                          color: 'var(--secondary-text-color)',
                        }}
                      >
                        <span
                          aria-hidden="true"
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: isConfigured ? '#22c55e' : 'var(--input-border)',
                          }}
                        />
                        {provLabel}
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* TODO: gate local provider to Studio+ tier when subscription enforcement is built */}
              <div key={provider} style={{ marginBottom: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--text-color)' }}>
                      {PROVIDER_LABELS[provider] || (provider.charAt(0).toUpperCase() + provider.slice(1))}
                    </span>
                    {provider === 'azure' && (
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'var(--secondary-text-color)' }}>
                        Copy each endpoint and API key from Azure, one config per endpoint
                      </span>
                    )}
                    {provider === 'aws' && (
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'var(--secondary-text-color)' }}>
                        Use a long-term Bedrock API key, or an access key and secret key, with the region your models run in. If you save both, the access key and secret key are used
                      </span>
                    )}
                    {provider === 'local' && (
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'var(--secondary-text-color)' }}>
                        Compatible with Ollama, LM Studio, vLLM, llama.cpp server, and any OpenAI-compatible inference server
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleAddProviderConfig(provider)}
                    style={{
                      fontSize: '0.78rem',
                      padding: '2px 10px',
                      cursor: 'pointer',
                      border: '1px solid var(--input-border)',
                      borderRadius: '4px',
                      background: 'var(--input-background)',
                      color: 'var(--text-color)',
                    }}
                  >
                    + Add config
                  </button>
                </div>

                {cfgList.map((cfg) => {
                  const isFirst = cfg.config_index === 1;
                  const focusPrefix = `${provider}-${cfg.config_index}`;
                  return (
                    <div
                      key={cfg.config_index}
                      style={{
                        border: '1px solid var(--input-border)',
                        borderRadius: '6px',
                        padding: '0.6rem 0.75rem',
                        marginBottom: '0.5rem',
                        background: 'var(--input-background)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                        <input
                          type="text"
                          value={cfg.config_name || ''}
                          onChange={(e) => handleConfigFieldChange(provider, cfg.config_index, 'config_name', e.target.value)}
                          placeholder={isFirst ? 'Label (optional, e.g. "Primary")' : `Config ${cfg.config_index} label`}
                          style={{
                            flex: 1,
                            fontSize: '0.82rem',
                            padding: '3px 7px',
                            border: '1px solid var(--input-border)',
                            borderRadius: '4px',
                            background: 'var(--background-color)',
                            color: 'var(--text-color)',
                          }}
                        />
                        {!isFirst && (
                          <button
                            onClick={() => handleDeleteProviderConfig(provider, cfg.config_index)}
                            title="Remove this config"
                            style={{
                              fontSize: '0.78rem',
                              padding: '2px 8px',
                              cursor: 'pointer',
                              border: '1px solid #e53e3e',
                              borderRadius: '4px',
                              background: 'transparent',
                              color: '#e53e3e',
                            }}
                          >
                            ✕
                          </button>
                        )}
                      </div>

                      {/* Server URL row — shown first for local; labeled "Server URL (required)" */}
                      {provider === 'local' && (
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.35rem' }}>
                          <span style={{ minWidth: '90px', fontSize: '0.8rem', color: 'var(--secondary-text-color)' }}>
                            Server URL
                            <span style={{ color: '#e53e3e', marginLeft: '2px' }}>*</span>
                          </span>
                          <input
                            type="text"
                            value={getInputValue(cfg.endpoint, `${focusPrefix}-endpoint`)}
                            onChange={(e) => handleConfigFieldChange(provider, cfg.config_index, 'endpoint', e.target.value)}
                            onFocus={() => setFocusedInput(`${focusPrefix}-endpoint`)}
                            onBlur={() => setFocusedInput(null)}
                            placeholder={isMasked(cfg.endpoint) ? 'Enter new server URL' : localServerExample()}
                            className={styles.input}
                            style={{ flex: 1 }}
                            autoComplete="off"
                          />
                          {cfg.endpoint && (
                            <button
                              type="button"
                              onClick={() => handleClearConfigField(provider, cfg.config_index, 'endpoint')}
                              title="Clear server URL"
                              style={{
                                fontSize: '0.75rem',
                                padding: '2px 8px',
                                cursor: 'pointer',
                                border: '1px solid var(--input-border)',
                                borderRadius: '4px',
                                background: 'transparent',
                                color: 'var(--secondary-text-color)',
                              }}
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      )}

                      {/* API Key row */}
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.35rem' }}>
                        <span style={{ minWidth: '90px', fontSize: '0.8rem', color: 'var(--secondary-text-color)' }}>
                          {provider === 'local' ? 'API Key (opt.)' : 'API Key'}
                        </span>
                        <input
                          type="password"
                          value={getInputValue(cfg.api_key, `${focusPrefix}-api_key`)}
                          onChange={(e) => handleConfigFieldChange(provider, cfg.config_index, 'api_key', e.target.value)}
                          onFocus={() => setFocusedInput(`${focusPrefix}-api_key`)}
                          onBlur={() => setFocusedInput(null)}
                          placeholder={
                            isMasked(cfg.api_key)
                              ? 'Enter new API key'
                              : provider === 'local'
                                ? 'Leave empty for Ollama; enter key if required'
                                : 'Enter API key'
                          }
                          className={styles.input}
                          style={{ flex: 1 }}
                          autoComplete="new-password"
                        />
                        {cfg.api_key && (
                          <button
                            type="button"
                            onClick={() => handleClearConfigField(provider, cfg.config_index, 'api_key')}
                            title="Clear API key"
                            style={{
                              fontSize: '0.75rem',
                              padding: '2px 8px',
                              cursor: 'pointer',
                              border: '1px solid var(--input-border)',
                              borderRadius: '4px',
                              background: 'transparent',
                              color: 'var(--secondary-text-color)',
                            }}
                          >
                            Clear
                          </button>
                        )}
                      </div>

                      {/* Endpoint row — hidden for local (Server URL row above replaces it) and own credentials */}
                      {provider !== 'local' && ownCreds.length === 0 && (
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: 0 }}>
                          <span style={{ minWidth: '90px', fontSize: '0.8rem', color: 'var(--secondary-text-color)' }}>Endpoint</span>
                          <input
                            type="password"
                            value={getInputValue(cfg.endpoint, `${focusPrefix}-endpoint`)}
                            onChange={(e) => handleConfigFieldChange(provider, cfg.config_index, 'endpoint', e.target.value)}
                            onFocus={() => setFocusedInput(`${focusPrefix}-endpoint`)}
                            onBlur={() => setFocusedInput(null)}
                            placeholder={
                              isMasked(cfg.endpoint)
                                ? 'Enter new endpoint'
                                : provider === 'azure'
                                  ? 'Copy from Azure'
                                  : 'https://api... (optional)'
                            }
                            className={styles.input}
                            style={{ flex: 1 }}
                            autoComplete="new-password"
                          />
                          {cfg.endpoint && (
                            <button
                              type="button"
                              onClick={() => handleClearConfigField(provider, cfg.config_index, 'endpoint')}
                              title="Clear endpoint"
                              style={{
                                fontSize: '0.75rem',
                                padding: '2px 8px',
                                cursor: 'pointer',
                                border: '1px solid var(--input-border)',
                                borderRadius: '4px',
                                background: 'transparent',
                                color: 'var(--secondary-text-color)',
                              }}
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      )}

                      {ownCreds.map((field) => (
                        <div key={field.name} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.35rem' }}>
                          <span style={{ minWidth: '90px', fontSize: '0.8rem', color: 'var(--secondary-text-color)' }}>
                            {field.label}
                          </span>
                          <input
                            type={field.type}
                            value={getInputValue(cfg[field.name], `${focusPrefix}-${field.name}`)}
                            onChange={(e) => handleConfigFieldChange(provider, cfg.config_index, field.name, e.target.value)}
                            onFocus={() => setFocusedInput(`${focusPrefix}-${field.name}`)}
                            onBlur={() => setFocusedInput(null)}
                            placeholder={isMasked(cfg[field.name]) ? `Enter new ${field.label.toLowerCase()}` : field.placeholder}
                            className={styles.input}
                            style={{ flex: 1 }}
                            autoComplete="new-password"
                          />
                          {cfg[field.name] && (
                            <button
                              type="button"
                              onClick={() => handleClearConfigField(provider, cfg.config_index, field.name)}
                              title={`Clear ${field.label}`}
                              style={{
                                fontSize: '0.75rem',
                                padding: '2px 8px',
                                cursor: 'pointer',
                                border: '1px solid var(--input-border)',
                                borderRadius: '4px',
                                background: 'transparent',
                                color: 'var(--secondary-text-color)',
                              }}
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </>
          );
        })()
      )}
      {/* Tool API Keys subsection */}
      {Array.isArray(toolApiKeys) && toolApiKeys.length > 0 && (
        <div style={{ marginTop: '1.25rem' }}>
          <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-color)' }}>
            Tool API Keys
          </h3>
          <p style={{ fontSize: '0.8rem', color: 'var(--secondary-text-color)', marginBottom: '0.75rem' }}>
            Supply your own API keys for external search tools used in workflow nodes.
          </p>
          {toolApiKeys.map(({ tool_name, has_key }) => {
            const label = TOOL_LABELS[tool_name] || tool_name;
            const inputVal = toolApiKeyInputs?.[tool_name] || '';
            return (
              <div
                key={tool_name}
                style={{
                  border: '1px solid var(--input-border)',
                  borderRadius: '6px',
                  padding: '0.6rem 0.75rem',
                  marginBottom: '0.5rem',
                  background: 'var(--input-background)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.88rem', color: 'var(--text-color)' }}>
                    {label}
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.75rem', color: 'var(--secondary-text-color)' }}>
                    <span
                      aria-hidden="true"
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: has_key ? '#22c55e' : 'var(--input-border)',
                      }}
                    />
                    {has_key ? 'Key configured' : 'No key set'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="password"
                    value={inputVal}
                    onChange={(e) =>
                      setToolApiKeyInputs((prev) => ({
                        ...prev,
                        [tool_name]: e.target.value,
                      }))
                    }
                    placeholder={has_key ? 'Enter new API key to replace' : 'Enter API key'}
                    className={styles.input}
                    style={{ flex: 1 }}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => handleSaveToolApiKey(tool_name)}
                    style={{
                      fontSize: '0.78rem',
                      padding: '4px 12px',
                      cursor: 'pointer',
                      border: '1px solid var(--input-border)',
                      borderRadius: '4px',
                      background: 'var(--input-background)',
                      color: 'var(--text-color)',
                    }}
                  >
                    Save
                  </button>
                  {has_key && (
                    <button
                      type="button"
                      onClick={() => handleRemoveToolApiKey(tool_name)}
                      title={`Remove ${label} key`}
                      style={{
                        fontSize: '0.78rem',
                        padding: '4px 10px',
                        cursor: 'pointer',
                        border: '1px solid #e53e3e',
                        borderRadius: '4px',
                        background: 'transparent',
                        color: '#e53e3e',
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ApiKeysTab;
